/*
  Licensed under the Apache License, Version 2.0 (the "License"); you may not use
  this file except in compliance with the License. You may obtain a copy of the
  License at

      https://www.apache.org/licenses/LICENSE-2.0

  Unless required by applicable law or agreed to in writing, software distributed
  under the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR
  CONDITIONS OF ANY KIND, either express or implied. See the License for the
  specific language governing permissions and limitations under the License.
*/
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import { localeMapping } from "../../locales";
import { DescriptionFields, Hint, LearnMoreLink, PartialDate } from "./model";

export const getDateFromPartialDate = (
  partialDate: PartialDate
): Date | null => {
  if (partialDate?.year && partialDate?.month && partialDate?.day) {
    return new Date(partialDate.year, partialDate.month - 1, partialDate.day);
  }

  if (partialDate?.year && partialDate?.month) {
    return new Date(partialDate.year, partialDate.month - 1);
  }

  if (partialDate?.year) {
    return new Date(partialDate.year, 0, 1);
  }

  return null;
};

export const getStringDateFromPartialDate = (
  partialDate: PartialDate,
  language?: string
): string => {
  const lang = language || i18n.language || "en";
  const locale = localeMapping[lang] || lang;

  if (partialDate?.year && partialDate?.month && partialDate?.day) {
    const date = new Date(
      partialDate.year,
      partialDate.month - 1,
      partialDate.day
    );
    const formatter = new Intl.DateTimeFormat(locale, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
    const parts = formatter.formatToParts(date);
    const day = parts.find((p) => p.type === "day")?.value;
    const month = parts.find((p) => p.type === "month")?.value;
    const year = parts.find((p) => p.type === "year")?.value;
    const capitalizedMonth = month
      ? month.charAt(0).toUpperCase() + month.slice(1)
      : "";
    return `${day} ${capitalizedMonth} ${year}`;
  }

  if (partialDate?.year && partialDate?.month) {
    const date = new Date(partialDate.year, partialDate.month - 1);
    const formatter = new Intl.DateTimeFormat(locale, {
      year: "numeric",
      month: "long",
    });
    const parts = formatter.formatToParts(date);
    const month = parts.find((p) => p.type === "month")?.value;
    const year = parts.find((p) => p.type === "year")?.value;
    const capitalizedMonth = month.charAt(0).toUpperCase() + month.slice(1);
    return `${capitalizedMonth} ${year}`;
  }

  if (partialDate?.year) {
    return partialDate?.year.toString();
  }

  return "";
};

export const getExtraMetadataItems = (
  descriptionFields: DescriptionFields,
  t: (key: string) => string,
  language?: string
): Array<{ label: string; value: any }> => {
  const { resolutionMeters, dataVersion, startDate, endDate, publicationDate } =
    descriptionFields;

  const startDateString = getStringDateFromPartialDate(startDate, language);
  const endDateString = getStringDateFromPartialDate(endDate, language);
  const publicationDateString = getStringDateFromPartialDate(
    publicationDate,
    language
  );

  const timePeriodValue =
    startDateString && endDateString
      ? `${startDateString} - ${endDateString}`
      : startDateString || endDateString;

  return [
    ...(resolutionMeters
      ? [
          {
            label: t("Resolution"),
            value: `${resolutionMeters.toString()} ${t("m")}`,
          },
        ]
      : []),
    ...(dataVersion ? [{ label: t("Data Version"), value: dataVersion }] : []),
    ...(timePeriodValue
      ? [{ label: t("Time Period"), value: timePeriodValue }]
      : []),
    ...(publicationDateString
      ? [
          {
            label: t("Publication Date"),
            value: publicationDateString,
          },
        ]
      : []),
  ];
};

export const getHtmlLink = (learnMoreLink: LearnMoreLink): string => {
  return `<a target="_blank" href="${learnMoreLink.url}">${learnMoreLink.content}</a>`;
};

export const getLearnMoreContent = (
  readMoreLinks: LearnMoreLink[] | null,
  downloadLinks: LearnMoreLink[] | null
) => {
  return [
    ...(readMoreLinks || []).map((readMoreLink) => getHtmlLink(readMoreLink)),
    ...(downloadLinks || []).map((downloadLink) => getHtmlLink(downloadLink)),
  ].join("<br />");
};

export const getLearnMoreURLs = (learnMoreLinks: LearnMoreLink[] | null) => {
  return `<div>${(learnMoreLinks || [])
    .map((link) => `<a target="_blank" href="${link.url}">${link.url}</a>`)
    .join("<br /><br />")}</div>`;
};

export const getConsolidatedSearchHint = (hint: Hint) => {
  const { t } = useTranslation();

  return [
    ...(hint.description ? [{ title: "", content: hint.description }] : []),
    ...(hint.sources ? [{ title: t("Source(s)"), content: hint.sources }] : []),
    ...(hint.citation
      ? [{ title: t("Suggested Citation"), content: hint.citation }]
      : []),
    ...(hint.license ? [{ title: t("License"), content: hint.license }] : []),
  ].slice(0, 1);
};
